import {parseParams, parseIncludes} from './api';
import {prepareContactProps} from './agency';
import {sortArrByDateDesc} from './array';
import {isArray} from './boolean';
import {
  formUtcToLocalDisplayFormat,
  formUtcToLocalISOString,
  getDate,
  getDateByNoOfDays,
  formUtcToLocal,
} from './date';
import {getObjectProp} from './object';
import {preparePropertyProps} from './property';

/**
 * Checks if the viewing date is in future or current.
 *
 * @param {string} dateStr
 * @returns {bool}
 */
export const checkIfViewingIsUpcoming = dateStr => {
  if (!dateStr) {
    return false;
  }
  return formUtcToLocal(dateStr).isSameOrAfter(getDate());
};

/**
 * Creates the api request params for viewings get list api.
 *
 * @param {object} params
 * @returns {string}
 */
export const prepareGetRequestParams = params => {
  const defaults = {
    addfields: parseIncludes([
      'agency',
      'tenant_agent_data',
      'belongs_to_agency_admin',
      'property',
      'tenancy_application_url',
      'contact',
      'updated_at',
    ]),
    include: 'viewing',
    sort: 'viewing.date_time',
    limit: '100',
    ...params,
  };
  return parseParams(defaults);
};

/**
 * Check if viewing can be confirmed
 *
 * @param {object} data
 * @returns {bool}
 */
export const prepareViewingConfirmable = data => {
  const status = getObjectProp(data, 'status');
  const viewingDate = getObjectProp(data, 'viewing.date_time');
  const isInUpcoming24hrs = formUtcToLocal(viewingDate).isBetween(
    getDate(),
    getDateByNoOfDays(1)
  );
  if (isInUpcoming24hrs && status !== 'cancelled' && status !== 'confirmed') {
    return true;
  }
  return false;
};

/**
 * Creates the status of a viewing booking.
 *
 * @param {string} status
 * @returns {string}
 */
export const prepareViewingStatus = data => {
  const attended = !data ? undefined : data.attended;
  const status = getObjectProp(data, 'status');
  const viewingDate = getObjectProp(data, 'viewing.date_time');
  if (attended === 0 && getDate('', true).isAfter(viewingDate)) {
    return 'not_attended';
  }
  if (status === 'cancelled') {
    return 'cancelled';
  }
  if (status === 'confirmed') {
    return 'confirmed';
  }
  return 'booked';
};

/**
 * Prepares the object of viewing details props from the api response.
 *
 * @param {object} data
 * @returns {object}
 */
export const prepareViewingProps = data => {
  const utcDateTime = getObjectProp(data, 'viewing.date_time');
  const agentEmail = getObjectProp(data, 'tenant_agent_data.email');
  const agentPhone = getObjectProp(data, 'tenant_agent_data.contact_phone');
  const hasNotification = getObjectProp(data, 'updates') || null;
  const isUpcoming = checkIfViewingIsUpcoming(utcDateTime);
  const {primaryImage, ...property} = preparePropertyProps(
    getObjectProp(data, 'property')
  );
  const scheduledAt = formUtcToLocalDisplayFormat(utcDateTime);
  const [scheduledAtDate = '', scheduledAtTime = ''] = (scheduledAt || '')
    .split(', ')
    .map(part => part.trim())
    .filter(Boolean);

  return {
    agentEmail,
    agencyLogo: getObjectProp(data, 'agency.logo'),
    agencyName: getObjectProp(data, 'agency.company_name_tenancy_application'),
    agentName: getObjectProp(data, 'tenant_agent_data.fullname'),
    agentPhone,
    applicationId: getObjectProp(data, 'application_id'),
    contact: prepareContactProps(getObjectProp(data, 'contact', {})),
    id: getObjectProp(data, 'id'),
    isConfirmable: prepareViewingConfirmable(data),
    isUpcoming,
    photoSrc: primaryImage,
    updates: getObjectProp(data, 'updates') || null,
    hasNotification: !!hasNotification && isUpcoming,
    hasApplication: !!getObjectProp(data, 'application_id'),
    hasApplicationLink: !!getObjectProp(data, 'application_link_sent_at'),
    status: prepareViewingStatus(data),
    scheduledAt,
    scheduledAtDate,
    scheduledAtTime,
    scheduledAtLocal: formUtcToLocalISOString(utcDateTime),
    scheduledAtUtc: utcDateTime,
    tenancyApplicationUrl: getObjectProp(data, 'tenancy_application_url'),
    tenantFeedback: getObjectProp(data, 'tenant_feedback'),
    canLeaveFeedback: prepareCanLeaveFeedback(data),
    property,
    updatedAt: formUtcToLocalDisplayFormat(
      getObjectProp(data, 'updated_at', null)
    ),
  };
};

/**
 * Creates the list of viewings to parse as props.
 *
 * @param {array} data
 * @returns {array}
 */
export const prepareViewingListProps = data =>
  (isArray(data) &&
    data.length > 0 &&
    data.map(item => prepareViewingProps(item))) ||
  [];

/**
 * Creates the list of upcoming viewings to parse as props.
 *
 * @param {array} data
 * @returns {array}
 */
export const prepareUpcomingViewingListProps = data =>
  prepareViewingListProps(data).filter(viewing => viewing.isUpcoming);

/**
 * Creates the list of past viewings to parse as props.
 *
 * @param {array} data
 * @returns {array}
 */
export const preparePastViewingListProps = data =>
  sortArrByDateDesc(
    prepareViewingListProps(data).filter(viewing => !viewing.isUpcoming),
    'scheduledAtUtc'
  );

/**
 * Creates the list of past viewings to parse as props.
 *
 * @param {array} data
 * @returns {boolean}
 */
export const prepareCanLeaveFeedback = data =>
  !getObjectProp(data, 'tenant_feedback') &&
  getObjectProp(data, 'attended') === 1 &&
  getObjectProp(data, 'agency.enable_tenant_feedback_notification') === 1;
